import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, X, Check, Trash2, Send } from "lucide-react";

const HOURS = ["06","07","08","09","10","11","12","13","14","15","16","17","18","19","20","21","22","23","00","01","02","03","04","05"];

/* Start-of-week: Monday. */
const startOfWeek = (d) => {
  const n = new Date(d);
  const day = (n.getDay() + 6) % 7; // Mon=0
  n.setDate(n.getDate() - day);
  n.setHours(0, 0, 0, 0);
  return n;
};
const fmtDate = (d) => d.toISOString().slice(0, 10);
const dayLabel = (d) => d.toLocaleDateString("en-PH", { weekday: "short", day: "2-digit", month: "short" });

export default function SchedulePage() {
  const { branchId, hasPerm } = useAuth();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [editing, setEditing] = useState(null); // { date, employee_id?, ...shift }

  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart); d.setDate(d.getDate() + i); arr.push(d);
    }
    return arr;
  }, [weekStart]);

  const load = async () => {
    if (!branchId) return;
    try {
      const [emp, sh] = await Promise.all([
        employees.length ? Promise.resolve({ data: employees }) : api.get("/employees", { params: { branch_id: branchId, active: true } }),
        api.get("/shifts", { params: { branch_id: branchId, date_from: fmtDate(days[0]), date_to: fmtDate(days[6]) } }),
      ]);
      if (!employees.length) setEmployees(emp.data);
      setShifts(sh.data);
    } catch (_) {}
  };
  useEffect(() => { load(); }, [branchId, weekStart]);

  const grid = useMemo(() => {
    // grid[employee_id][day_index] = [shifts]
    const g = {};
    for (const s of shifts) {
      const dayIdx = days.findIndex((d) => fmtDate(d) === s.date);
      if (dayIdx < 0 || !s.employee_id) continue;
      g[s.employee_id] = g[s.employee_id] || {};
      g[s.employee_id][dayIdx] = g[s.employee_id][dayIdx] || [];
      g[s.employee_id][dayIdx].push(s);
    }
    return g;
  }, [shifts, days]);

  const unassigned = useMemo(() => shifts.filter((s) => !s.employee_id), [shifts]);

  const nav = (delta) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + delta * 7); setWeekStart(d);
  };

  const openNew = (date, employee_id) => {
    setEditing({
      date: fmtDate(date), employee_id: employee_id || null,
      start_at: `${fmtDate(date)}T18:00`,
      end_at:   `${fmtDate(date)}T23:00`,
      notes: "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = {
      branch_id: branchId,
      date: editing.date,
      start_at: new Date(editing.start_at).toISOString(),
      end_at:   new Date(editing.end_at).toISOString(),
      employee_id: editing.employee_id || null,
      notes: editing.notes,
    };
    try {
      if (editing.id) {
        await api.patch(`/shifts/${editing.id}`, {
          start_at: body.start_at, end_at: body.end_at,
          employee_id: body.employee_id, notes: body.notes,
        });
        toast.success("Shift updated");
      } else {
        await api.post("/shifts", body);
        toast.success("Shift added");
      }
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeShift = async (id) => {
    if (!window.confirm("Delete this shift?")) return;
    try {
      await api.delete(`/shifts/${id}`);
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const publishWeek = async () => {
    try {
      const { data } = await api.post("/shifts/publish", null, {
        params: { branch_id: branchId, date_from: fmtDate(days[0]), date_to: fmtDate(days[6]) },
      });
      toast.success(`Published ${data.published} shift${data.published === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="schedule-page">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">HR · Weekly schedule</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Roster grid</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Click any cell to plan a shift. Publish the week when it's locked — staff sees only published shifts and payroll picks them up automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => nav(-1)} data-testid="sch-prev-week" className="btn-pill btn-pill-outline h-10 px-3"><ChevronLeft size={14}/></button>
          <div className="tiny-label ink text-center mono min-w-[180px]" data-testid="sch-week-label">
            {dayLabel(days[0])} → {dayLabel(days[6])}
          </div>
          <button onClick={() => nav(1)} data-testid="sch-next-week" className="btn-pill btn-pill-outline h-10 px-3"><ChevronRight size={14}/></button>
          {hasPerm("attendance.manage") && (
            <button onClick={publishWeek} data-testid="sch-publish" className="btn-pill btn-pill-solid h-10 ml-2"><Send size={13}/> Publish week</button>
          )}
        </div>
      </div>

      {!branchId && (
        <div className="mt-14 border hairline-soft rounded-md p-8 text-center ink-subtle text-sm">
          Select a branch first.
        </div>
      )}

      {branchId && (
        <div className="mt-10 border hairline-soft rounded-lg overflow-hidden animate-fade-up animate-fade-up-1" data-testid="sch-grid">
          {/* header row */}
          <div className="grid grid-cols-8 border-b hairline-soft bg-foreground/[0.03]">
            <div className="p-3 tiny-label ink-subtle">Employee</div>
            {days.map((d, i) => (
              <div key={i} className={`p-3 tiny-label border-l hairline-soft ${fmtDate(d) === fmtDate(new Date()) ? "ink font-bold" : "ink-subtle"}`}>
                {dayLabel(d)}
              </div>
            ))}
          </div>
          {/* rows */}
          {employees.length === 0 && (
            <div className="p-8 text-center ink-subtle text-sm">No active employees on this branch. Add some in Employees first.</div>
          )}
          {employees.map((emp) => (
            <div key={emp.id} className="grid grid-cols-8 border-b hairline-soft last:border-b-0 hover:bg-foreground/[0.02] transition-colors" data-testid={`sch-row-${emp.id}`}>
              <div className="p-3 flex items-center gap-2 border-r hairline-soft min-w-0">
                <div className="h-7 w-7 rounded-full bg-foreground text-background flex items-center justify-center text-[9px] uppercase tracking-widest font-bold shrink-0">
                  {emp.name.split(" ").map((n)=>n[0]).slice(0,2).join("")}
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{emp.name}</div>
                  <div className="text-[10px] ink-subtle mono">{emp.code} · {emp.position}</div>
                </div>
              </div>
              {days.map((d, i) => {
                const cellShifts = grid[emp.id]?.[i] || [];
                return (
                  <button key={i} type="button"
                          onClick={() => cellShifts[0] ? setEditing({ ...cellShifts[0], date: cellShifts[0].date }) : hasPerm("attendance.manage") && openNew(d, emp.id)}
                          data-testid={`sch-cell-${emp.id}-${i}`}
                          className="border-l hairline-soft p-2 text-left min-h-[72px] hover:bg-foreground/[0.06] transition-colors">
                    {cellShifts.length === 0 ? (
                      <div className="ink-subtle text-[11px] mono opacity-0 hover:opacity-100 transition-opacity">+ add</div>
                    ) : cellShifts.map((s) => (
                      <div key={s.id} className={`text-[10px] rounded-md px-2 py-1 mb-1 border ${
                        s.published ? "bg-foreground text-background border-foreground" : "hairline text-foreground"
                      }`}>
                        <div className="mono font-bold">
                          {new Date(s.start_at).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
                          {" → "}
                          {new Date(s.end_at).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                        {s.notes && <div className="opacity-70 truncate">{s.notes}</div>}
                      </div>
                    ))}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {branchId && unassigned.length > 0 && (
        <div className="mt-10 animate-fade-up animate-fade-up-2">
          <div className="section-eyebrow mb-3">Unassigned this week</div>
          <div className="border-y hairline-soft divide-y hairline-soft">
            {unassigned.map((s) => (
              <button key={s.id} onClick={() => setEditing({ ...s })} className="w-full text-left grid grid-cols-12 py-3 items-center hover:bg-foreground/[0.03]">
                <div className="col-span-3 tiny-label">{new Date(s.start_at).toLocaleDateString("en-PH", { weekday: "short", day: "2-digit", month: "short" })}</div>
                <div className="col-span-3 mono text-sm ink">
                  {new Date(s.start_at).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })} → {new Date(s.end_at).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="col-span-3 tiny-label ink-subtle">{s.position || "any"}</div>
                <div className="col-span-3 text-xs ink-subtle italic truncate">{s.notes}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Shift drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="sch-shift-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditing(null)}/>
          <form onSubmit={submit} className="relative w-full max-w-md surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="section-eyebrow">HR · Shift</div>
                <h2 className="mt-2 display-heading text-2xl ink">{editing.id ? "Edit shift" : "New shift"}</h2>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="tiny-label ink-subtle hover:ink"><X size={16}/></button>
            </div>

            <F label="Employee">
              <select value={editing.employee_id || ""} onChange={(e) => setEditing({ ...editing, employee_id: e.target.value || null })}
                      className="i" data-testid="sch-form-emp">
                <option value="">— Unassigned —</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.position})</option>)}
              </select>
            </F>

            <div className="grid grid-cols-2 gap-3">
              <F label="Start">
                <input required type="datetime-local" value={editing.start_at.slice(0,16)}
                       onChange={(e) => setEditing({ ...editing, start_at: e.target.value })}
                       className="i mono" data-testid="sch-form-start"/>
              </F>
              <F label="End">
                <input required type="datetime-local" value={editing.end_at.slice(0,16)}
                       onChange={(e) => setEditing({ ...editing, end_at: e.target.value })}
                       className="i mono" data-testid="sch-form-end"/>
              </F>
            </div>

            <F label="Notes"><input value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="i" data-testid="sch-form-notes"/></F>

            {editing.id && (
              <div className="mt-6 flex items-center justify-between">
                <button type="button" onClick={() => removeShift(editing.id)}
                        data-testid="sch-form-delete"
                        className="text-[10px] uppercase tracking-[0.24em] font-bold text-red-500 hover:underline">
                  <Trash2 size={12} className="inline -mt-0.5 mr-1"/> Delete shift
                </button>
                <span className={`text-[9px] uppercase tracking-[0.22em] font-bold ${editing.published ? "text-emerald-500" : "text-foreground/50"}`}>
                  {editing.published ? "Published" : "Draft"}
                </span>
              </div>
            )}

            <div className="mt-6 flex gap-2">
              <button type="button" onClick={() => setEditing(null)} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
              <button type="submit" data-testid="sch-form-submit" className="btn-pill btn-pill-solid h-11 flex-1"><Check size={13}/> Save</button>
            </div>

            <style>{`.i { margin-top: 6px; height: 42px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 0 14px; color: hsl(var(--foreground)); font-size: 13px; }`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

function F({ label, children }) {
  return <label className="block mt-4"><div className="tiny-label">{label}</div>{children}</label>;
}
